import { forwardRef, useState } from "react";
import { cn } from "../../lib/cn";

/**
 * 공통 입력 한 칸.
 *
 * 칸을 누르면 안내 글(placeholder)이 사라진다(사용자 지시 2026-09-16). 회색 예시 글이
 * 적는 동안 남아 있으면 이미 값이 든 칸으로 보여, 무엇을 적어야 하는지보다 무엇이 적혀
 * 있는지를 먼저 헷갈린다.
 *
 * 같은 이유로 예시에는 「ex)」를 붙인다 — 「EVC-7000S」만 적혀 있으면 값인지 예시인지
 * 가릴 길이 없다. 「…검색」·「…입력하세요」처럼 시키는 말은 예시가 아니라 그대로 둔다.
 */

/** 안내 글에 「ex)」를 붙인다. 이미 붙었거나 시키는 말이면 그대로. */
export function exHint(placeholder?: string): string | undefined {
  const s = placeholder?.trim();
  if (!s || s === "-") return placeholder;
  if (/^ex\)/i.test(s)) return s;
  if (/^예\s*[:：]/.test(s)) return s.replace(/^예\s*[:：]\s*/, "ex) ");
  // 시키는 말·안내문은 예시가 아니다.
  if (/(검색|입력|선택|고르세요|주세요|하세요|씁니다|합니다|기록|추가|비움)/.test(s)) return s;
  // 문장 안에 이미 예시가 들어 있으면 겹쳐 붙이지 않는다.
  if (/예\s*[:：]/.test(s)) return s;
  return `ex) ${s}`;
}
export const Input = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  ({ className, type, placeholder, onFocus, onBlur, ...props }, ref) => {
    const [focused, setFocused] = useState(false);
    return (
      <input
        ref={ref}
        type={type}
        placeholder={focused ? "" : exHint(placeholder)}
        onFocus={(e) => { setFocused(true); onFocus?.(e); }}
        onBlur={(e) => { setFocused(false); onBlur?.(e); }}
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";
